import { Injectable, inject } from "@angular/core";
import { BaseService } from "../../../@core/services/base.service";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../../environments/environment.dev"; // Adjusted path
import { lastValueFrom } from "rxjs";
import { IAttachment, IUser, ResponseJson } from "../../../@core/models/model";
import { IThreadMetadata, IThread, ITokenLimitsAgent, LLM_PROVIDERS } from "../models/model";
import { DOCUMENT_MODEL, EMBEDDINGS_GENERATION_STATUS } from "../../../contract-management/models/contract-management.model";
import { ChatMessagePayload, IMessageFeedback, MESSAGE_FEEDBACK, PLAN_FEEDBACK, IAgentMemory } from "../models/types";
import { FILE_TYPE } from "../../../@core/models/enums";
import { CONNECTOR_PROVIDERS, TOOLKIT_PROVIDERS } from "../constants/tool.constants";
import { PromptLaunchService } from "./prompt-launch.service";

export interface IAttachedFile {
    _id: string;
    name: string;
    docModel: DOCUMENT_MODEL;
    refDocModel?: DOCUMENT_MODEL;
    status: EMBEDDINGS_GENERATION_STATUS;
    type: FILE_TYPE;
    attachmentData?: IAttachment;
    metadata?: Record<string, any>;
}

export interface IFileVersion {
    versionId: string;
    name: string;
}

export interface IModelInfo {
    max_requests: number;
    max_tokens: number;
    model: string;
}


export interface IThreadFilters {
    searchValue?: string;
}
@Injectable({
    providedIn: 'root'
})
export class GeneralAgentService extends BaseService {
    apiUrl = environment.apiUrl + '/general-agent';
    private promptLaunchService = inject(PromptLaunchService);

    constructor(protected override http: HttpClient) {
        super(http);
    }
    runThread(data: ChatMessagePayload) {
        // Attach library prompt ID (and clear it) so the backend skips re-enhancement,
        // regardless of which component (NewChatComponent or ActiveThreadComponent) calls this.
        const pendingLibraryId = this.promptLaunchService.pendingPromptId();
        if (pendingLibraryId) {
            this.promptLaunchService.clear();
            (data as ChatMessagePayload & { selectedPromptId?: string }).selectedPromptId = pendingLibraryId;
        }
        return lastValueFrom(this.http.post<ResponseJson<{ thread: IThread; threadMetadata: IThreadMetadata[] }>>(this.apiUrl + '/chat', data));
    }
    getThreads(page: number = 1, limit: number = 20, filters?: IThreadFilters) {
        /*
          INSTRUCTIONS:
          Must use lastValueFrom to convert the observable to a promise, because tanstack query expects a promise
          Ex: return lastValueFrom(this.get<{ name: string, threadId: string }[]>(this.apiUrl + 'threads'))
          */
        const skip = (page - 1) * limit;
        return lastValueFrom(this.post<{ threads: { title: string, _id: string, isOwner: boolean, threadMetadataId: string, createdAt: string }[], total: number }>(this.apiUrl + '/threads', { skip, limit, filters }))
    }

    getThread(generalAgentThreadId: string) {
        return lastValueFrom(this.get<{
            thread: IThread;
            threadMetadata: IThreadMetadata[];
            conversation: { messages: any[] };
            token_limits_agent: ITokenLimitsAgent;
            messageFeedbacks: IMessageFeedback[];
            pendingFiles?: IAttachedFile[];
            pendingEnhancement?: { markdown: string } | null;
            pendingReuseCandidates?: any[] | null;
            isOwner?: boolean;
            permissions?: { canView: boolean; canEdit: boolean; canComment: boolean; restricted: boolean };
        }>(this.apiUrl + `/${generalAgentThreadId}`));
    }

    saveDraftPrompt(threadId: string, prompt: string) {
        return this.put<null>(this.apiUrl + `/${threadId}/draft-prompt`, { prompt });
    }

    saveDraftOnUnload(threadId: string, prompt: string): void {
        const token = localStorage.getItem('token');
        const url = `${this.apiUrl}/${threadId}/draft-prompt`;
        fetch(url, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: JSON.stringify({ prompt }),
            keepalive: true,
        });
    }

    updateThreadTitle(threadId: string, title: string) {
        return lastValueFrom(this.put<IThread>(this.apiUrl + `/update-thread-title`, { threadId, title }));
    }

    deleteThread(threadId: string, options?: { emptyUploadCleanup?: boolean }) {
        const params = options?.emptyUploadCleanup ? { emptyUploadCleanup: 'true' } : undefined;
        return lastValueFrom(this.delete<ResponseJson<any>>(this.apiUrl + `/${threadId}`, params));
    }

    attachFiles(formData: FormData) {
        return lastValueFrom(this.post<ResponseJson<{ thread: IThread; threadMetadata: IThreadMetadata[] }>>(this.apiUrl + '/attach-files', formData));
    }

    deleteUploadedFile(_id: string, threadMetadataId?: string) {
        return lastValueFrom(this.delete<{}>(this.apiUrl + `/delete-uploaded-file/${_id}`, { threadMetadataId }));
    }

    getThreadFiles(threadId: string) {
        return lastValueFrom(this.get<(IAttachment & { docModel: DOCUMENT_MODEL, refDocModel: DOCUMENT_MODEL, refFile: string })[]>(this.apiUrl + `/files/${threadId}`));
    }

    getFileVersions(filePath: string) {
        return lastValueFrom(this.get<IFileVersion[]>(this.apiUrl + `/file-versions`, { filePath }));
    }

    getSelectedVersionUrl(filePath: string, versionId: string) {
        return lastValueFrom(this.get<string>(this.apiUrl + `/version-url`, { filePath, versionId }));
    }

    submitFeedback(data: { threadId: string, messageId: string, feedback: MESSAGE_FEEDBACK, details: string, threadMetadataId: string }) {
        return this.post<IMessageFeedback>(this.apiUrl + `/message-feedback`, data);
    }

    pauseThread(threadId: string, threadMetadataId: string) {
        return lastValueFrom(this.post<ResponseJson<any>>(this.apiUrl + `/pause-thread/${threadId}/thread-metadata/${threadMetadataId}`));
    }

    /**
     * Resume a thread after an interrupt.
     * @param editedPlan - Edited payload. Shape depends on interrupt source:
     *   - PromptEnhancerMiddleware: `{ markdown: string }`
     *   - write_todos HITL: `{ todos: [...] }`
     */
    resumeThread(
        threadId: string,
        planFeedback?: PLAN_FEEDBACK,
        toolParams?: any[],
        selectedProvider?: LLM_PROVIDERS,
        editedPlan?: Record<string, any>,
        revisionFeedback?: string,
        /**
         * Decision made on a PROMPT_REUSE_SUGGESTION interrupt.
         * Forwarded straight to Python so PromptEnhancerMiddleware can resume correctly.
         */
        reuseDecision?: { action: 'use_existing' | 'generate_new' | 'reject'; selected_prompt_id?: string },
    ) {
        return lastValueFrom(this.post<ResponseJson<any>>(this.apiUrl + `/resume-thread/${threadId}`, {
            planFeedback,
            toolParams,
            selectedProvider,
            editedPlan,
            ...(revisionFeedback ? { revisionFeedback } : {}),
            ...(reuseDecision ? { reuseDecision } : {}),
        }));
    }


    editCanvasManually(attachmentId: string, editedContent: string, messageId: string, threadMetadataId: string) {
        return lastValueFrom(this.post<ResponseJson<any>>(this.apiUrl + `/edit-canvas-manually`, { attachmentId, editedContent, messageId, threadMetadataId }));
    }

    downloadCanvas(attachmentId: string, versionId: string) {
        return lastValueFrom(this.get<{ data: Uint8Array }>(this.apiUrl + `/download-canvas/${attachmentId}/${versionId}`));
    }

    getModels() {
        return this.get<{ [key in LLM_PROVIDERS]: IModelInfo }>(this.apiUrl + `/models`);
    }

    getMemories(continuationToken?: string, maxFiles = 10) {
        const params: any = { maxFiles: maxFiles.toString() };
        if (continuationToken) params.continuationToken = continuationToken;
        return this.get<{ memories: IAgentMemory[], nextToken: string | null }>(
            this.apiUrl + `/memories`, params
        );
    }

    updateMemory(s3Key: string, lineIndex: number, text: string) {
        return this.put<IAgentMemory[]>(this.apiUrl + `/update-memory`, { s3Key, lineIndex, text });
    }

    deleteMemory(s3Key: string, lineIndex: number) {
        return this.delete<IAgentMemory[]>(this.apiUrl + `/delete-memory`, { s3Key, lineIndex });
    }

    deleteAllMemories() {
        return this.delete<IAgentMemory[]>(this.apiUrl + `/delete-all-memories`);
    }

    listSkills() {
        return lastValueFrom(this.get<{ keys: string[] }>(this.apiUrl + '/list-skills'));
    }

    deleteSkill(path: string) {
        return lastValueFrom(this.delete<unknown>(this.apiUrl + '/delete-skill', { path }));
    }

    getSkillFileUrl(path: string) {
        return lastValueFrom(this.get<{ url: string }>(this.apiUrl + '/skill-file-url', { path }));
    }

    uploadSkillsFolder(formData: FormData) {
        return lastValueFrom(this.post<{ uploadedFiles: number }>(this.apiUrl + '/upload-skills', formData));
    }

}
